<template>
  <div ref="containerRef" class="collapsible-article-container" :style="containerStyle">
    <div ref="elemRef" v-bind="$attrs" class="collapsible-article relative" :style="contentStyle">
      <slot></slot>
      <div v-show="isMaskShown" class="mask absolute w-full h-30 bottom-0 left-0 pointer-events-none"></div>
    </div>
    <div v-if="isExpandBtnShown && isClamped" v-show="!isExpanded" class="read-more flex justify-center">
      <etg-button type="text" size="small" icon="arrow-down" icon-position="right" @click="isExpanded = true">
        {{ props.expandBtnText }}
      </etg-button>
    </div>
    <div v-if="isCollapseBtnShown && isClamped" v-show="isExpanded" class="read-more flex justify-center">
      <etg-button type="text" size="small" icon="arrow-up" icon-position="right" @click="isExpanded = false">
        {{ props.collapseBtnText }}
      </etg-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StyleValue } from 'vue'

interface Props {
  isExpandBtnShown?: boolean
  expandBtnText?: string
  isCollapseBtnShown?: boolean
  collapseBtnText?: string
  lineNum?: number
  maxHeight?: number
  minContainerHeight?: number
  minContentHeight?: number
  mask?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isExpandBtnShown: true,
  expandBtnText: () => useI18n().t('general.view_all'),
  isCollapseBtnShown: false,
  collapseBtnText: () => useI18n().t('general.show_less'),
  lineNum: 3,
  maxHeight: 0,
  mask: false,
})

const isClamped = ref(false)
const isExpanded = ref(false)
const containerRef = ref<Nullable<HTMLElement>>(null)
const elemRef = ref<Nullable<HTMLElement>>(null)

const { height: containerHeight } = useElementSize(containerRef)
const { height: elemHeight } = useElementSize(elemRef)

const mode = computed(() => (props.maxHeight ? 'max-height' : 'line-clamp'))
const isMaskShown = computed(() => props.mask && isClamped.value && !isExpanded.value)

const containerStyle = computed(() => {
  const marginBottom = !!props.minContainerHeight && !!containerHeight.value ? Math.max(props.minContainerHeight - containerHeight.value, 0) : 0

  return { ...(!!marginBottom && { marginBottom: `${marginBottom}px` }) }
})
const contentStyle = computed(() => {
  const marginBottom = !!props.minContentHeight && !!elemHeight.value ? Math.max(props.minContentHeight - elemHeight.value, 0) : 0
  const baseStyle: StyleValue = { overflow: 'hidden', ...(!!marginBottom && { marginBottom: `${marginBottom}px` }) }

  if (mode.value === 'max-height') {
    return {
      ...baseStyle,
      maxHeight: isExpanded.value ? 'none' : `${props.maxHeight}px`,
    }
  } else if (mode.value === 'line-clamp') {
    return {
      ...baseStyle,
      display: isExpanded.value ? 'block' : '-webkit-box',
      'text-overflow': 'ellipsis',
      '-webkit-box-orient': 'vertical' as const,
      '-webkit-line-clamp': isExpanded.value ? 'none' : `${props.lineNum}`,
    }
  }
  return {}
})

const checkClamp = () => {
  if (elemRef.value) {
    if (mode.value === 'max-height') {
      isClamped.value = !isExpanded.value && elemRef.value.scrollHeight > props.maxHeight
    } else if (mode.value === 'line-clamp') {
      isClamped.value = elemRef.value.scrollHeight > elemRef.value.clientHeight
    }
  }
}

useEventListener('resize', checkClamp)

onMounted(() => {
  checkClamp()
  isExpanded.value = !isClamped.value
})
</script>

<style lang="scss" scoped>
.collapsible-article {
  .mask {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }
}
</style>
